import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import LinearGauge from '../common/LinearGauge';
import { dynamicText0Switch } from './AppSoilingAssessment';
import { ReactNode } from 'react';

type Props = {
  data: any;
};

interface RiskLabel {
  label: string;
  level_low: number;
  level_high: number;
  short_label: ReactNode;
  losses_label: ReactNode;
  color: string;
  description: string;
}

const indexLabels = {
  low_risk: {
    label: 'Low Risk',
    level_low: 0,
    level_high: 25,
    short_label: (
      <Typography color="text.primary" variant="h5" sx={{ color: 'rgba(66,185,114,0.8)', display: 'inline-block' }}>
        Low
      </Typography>
    ),
    losses_label: (
      <Typography color="text.primary" variant="h5" sx={{ color: 'rgba(66,185,114,0.8)', display: 'inline-block' }}>
        0-0.5%
      </Typography>
    ),
    color: 'rgba(66,185,114,0.8)',
    description: 'Requires less than one cleaning session per year on average​',
  },
  moderate_risk: {
    label: 'Moderate Risk',
    level_low: 25,
    level_high: 50,
    short_label: (
      <Typography color="text.primary" variant="h5" sx={{ color: 'rgba(255,185,29,0.8)', display: 'inline-block' }}>
        Moderate
      </Typography>
    ),
    losses_label: (
      <Typography color="text.primary" variant="h5" sx={{ color: 'rgba(255,185,29,0.8)', display: 'inline-block' }}>
        0.5-1%
      </Typography>
    ),
    color: 'rgba(255,185,29,0.8)',
    description: 'It may require one cleaning session per year, depending on local conditions​',
  },
  high_risk: {
    label: 'High Risk',
    level_low: 50,
    level_high: 75,
    short_label: (
      <Typography color="text.primary" variant="h5" sx={{ color: 'rgba(190,106,20,0.8)', display: 'inline-block' }}>
        High
      </Typography>
    ),
    losses_label: (
      <Typography color="text.primary" variant="h5" sx={{ color: 'rgba(190,106,20,0.8)', display: 'inline-block' }}>
        1-2%
      </Typography>
    ),
    color: 'rgba(190,106,20,0.8)',
    description: 'Requires one or more cleaning sessions per year. It is recommended to install a soiling station​',
  },
  very_high_risk: {
    label: 'Very High Risk',
    level_low: 75,
    level_high: 100,
    short_label: (
      <Typography color="text.primary" variant="h5" sx={{ color: 'rgba(177,42,46,0.8)', display: 'inline-block' }}>
        Very High
      </Typography>
    ),
    losses_label: (
      <Typography color="text.primary" variant="h5" sx={{ color: 'rgba(177,42,46,0.8)', display: 'inline-block' }}>
        2-5%
      </Typography>
    ),
    color: 'rgba(177,42,46,0.8)',
    description:
      'Significant revenue losses expected due to soiling. Requires a soiling measurement campaign + expert analysis to optimize the mitigation strategy. An in-house cleaning solution (robots, tractor) may be necessary​',
  },
};

const GlobalSoilingIndex = ({ data }: Props) => {
  //@ts-ignore
  const soilingIndex: RiskLabel =
    indexLabels[
      Object.keys(indexLabels).filter(
        //@ts-ignore
        (key) => data?.scores?.global * 100 >= indexLabels[key].level_low && data?.scores?.global * 100 < indexLabels[key].level_high,
      )[0]
    ];
  const short_label = soilingIndex.short_label;
  const losses_label = soilingIndex.losses_label;

  return (
    <>
      <LinearGauge title={'Global Soiling Index'} value={data?.scores?.global} />
      <Card sx={{ mt: 2, p: 0, mb: 2 }}>
        <CardContent sx={{ p: 0, paddingBottom: '0px !important ' }}>
          <Grid container spacing={2} sx={{ p: 0 }}>
            <Grid item xs={4} sx={{ p: 0, height: '100%' }}>
              <Box sx={{ backgroundColor: soilingIndex.color, p: 5, color: 'white', fontSize: '2rem', textAlign: 'center' }}>
                {soilingIndex.label}
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.primary" variant="h5">
                  {soilingIndex.description}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card sx={{ mt: 2, p: 0, mb: 2 }}>
        <CardContent>
          <Typography color="text.primary" variant="h5" sx={{ mb: 2 }}>
            Based on historical rain, climatic conditions (including wind speed and humidity), and airborne particle concentration, your
            project is categorized with a {short_label} risk of soiling exposure.
          </Typography>
          <Typography color="text.primary" variant="h5" sx={{ mb: 2 }}>
            The expected theoretical production losses per year range between {losses_label} when compared to a system without soiling
          </Typography>
        </CardContent>
      </Card>
      {dynamicText0Switch(data)}
    </>
  );
};

export default GlobalSoilingIndex;
